import React, { Fragment, useState } from 'react';
import {
  Box,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { DictionaryWord } from 'app/types';
import { ResolvedTableSettings } from 'components/tableSettings';
import AudioButton from 'components/dictionaryView/AudioButton';
import { Checkbox, POSTag } from './primitives';
import { WordStatus, statusColor, statusOf } from './meta';
import StatusCell from './StatusCell';
import GroupHeader from './GroupHeader';
import { UseVocabularyWordsViewState } from './useVocabularyWordsView';

interface WorkCardsProps {
  view: UseVocabularyWordsViewState;
  tableSettings: ResolvedTableSettings;
  onSetStatus: (id: number, status: WordStatus) => void;
  onDeleteOne: (id: number) => void;
}

const EmptyState: React.FC = () => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'primary.tableBorder',
      borderRadius: 2,
      color: 'text.secondary',
      px: 3,
      py: 6,
      textAlign: 'center',
    }}
  >
    <SearchOffIcon sx={{ color: 'primary.tableBorder', fontSize: 34 }} />
    <Typography sx={{ fontSize: 15, fontWeight: 600, mt: 1 }}>
      {'Нічого не знайдено'}
    </Typography>
    <Typography sx={{ fontSize: 13.5, mt: 0.25 }}>
      {'Спробуй змінити фільтри або пошук'}
    </Typography>
  </Box>
);

const CardEditField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}> = ({ value, onChange, placeholder }) => (
  <TextField
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    size={'small'}
    onClick={(e) => e.stopPropagation()}
    sx={{
      width: '100%',
      '& .MuiInputBase-root': {
        bgcolor: 'background.default',
        fontSize: 14,
        height: 34,
      },
      '& fieldset': { borderColor: 'primary.main' },
    }}
  />
);

const WordCard: React.FC<{
  word: DictionaryWord;
  view: UseVocabularyWordsViewState;
  tableSettings: ResolvedTableSettings;
  onSetStatus: (id: number, status: WordStatus) => void;
  onDeleteOne: (id: number) => void;
}> = ({ word, view, tableSettings, onSetStatus }) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const [hover, setHover] = useState(false);
  const selected = !!view.selected[word.id];
  const editing = view.isEditing(word.id);
  const draft = editing ? view.draftFor(word.id) : null;
  const highlighted = view.highlightId === word.id;

  const colVisible = (key: string) => {
    const c = tableSettings.columns.find((x) => x.key === key);
    return c ? c.visible : true;
  };

  const status = statusOf(word);
  const accent =
    status === 'NEW' ? null : statusColor(status, mode);

  const bg = highlighted
    ? mode === 'light'
      ? 'rgba(39,204,188,0.14)'
      : 'rgba(63,224,206,0.18)'
    : selected
    ? mode === 'light'
      ? 'rgba(59,44,147,0.07)'
      : 'rgba(155,124,255,0.14)'
    : 'background.paper';

  return (
    <Box
      onClick={() => (editing ? undefined : view.toggle(word.id))}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        bgcolor: bg,
        border: '1px solid',
        borderColor: selected
          ? 'primary.main'
          : hover
          ? 'primary.tableBorder'
          : 'primary.tableBorder',
        borderLeft: '4px solid',
        borderLeftColor: accent ?? 'primary.tableBorder',
        borderRadius: 2,
        cursor: editing ? 'default' : 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        p: '16px 18px',
        position: 'relative',
        transition: 'all .14s ease',
      }}
    >
      <Stack direction={'row'} spacing={1.25} alignItems={'flex-start'}>
        <Box sx={{ pt: '3px' }}>
          <Checkbox checked={selected} onClick={() => view.toggle(word.id)} />
        </Box>
        <AudioButton word={word.word ?? ''} size={34} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 800,
              lineHeight: 1.2,
              overflowWrap: 'anywhere',
            }}
          >
            {word.partOfSpeech === 'VERB' ? `to ${word.word}` : word.word}
          </Typography>
          {(colVisible('transcription') || colVisible('partOfSpeech')) && (
            <Stack
              direction={'row'}
              alignItems={'center'}
              spacing={1}
              sx={{ minHeight: 20, mt: 0.375 }}
            >
              {colVisible('transcription') && word.transcription && (
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontFamily: 'ui-monospace, monospace',
                    fontSize: 13.5,
                  }}
                >
                  {word.transcription}
                </Typography>
              )}
              {colVisible('partOfSpeech') && <POSTag pos={word.partOfSpeech} />}
            </Stack>
          )}
        </Box>

      </Stack>

      {editing ? (
        <Stack spacing={1} sx={{ pl: 5.5 }}>
          <CardEditField
            value={draft!.translation}
            onChange={(v) => view.setDraft(word.id, { translation: v })}
            placeholder={'переклад'}
          />
          <CardEditField
            value={draft!.definition}
            onChange={(v) => view.setDraft(word.id, { definition: v })}
            placeholder={'визначення'}
          />
          <Stack direction={'row'} spacing={0.75} justifyContent={'flex-end'}>
            <IconButton
              size={'small'}
              onClick={(e) => {
                e.stopPropagation();
                view.cancelEdit(word.id);
              }}
              sx={{
                border: '1px solid',
                borderColor: 'primary.tableBorder',
                color: 'text.secondary',
                height: 32,
                width: 32,
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size={'small'}
              onClick={(e) => {
                e.stopPropagation();
                view.saveEdit(word.id);
              }}
              sx={{
                bgcolor: 'primary.main',
                color: 'common.white',
                height: 32,
                width: 32,
                '&:hover': { bgcolor: 'primary.main', opacity: 0.92 },
              }}
            >
              <CheckIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Stack>
      ) : (
        <Box sx={{ pl: 5.5 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 0.625 }}>
            {word.translation}
          </Typography>
          {colVisible('definition') && word.definition && (
            <Typography
              sx={{ color: 'text.secondary', fontSize: 14, lineHeight: 1.45 }}
            >
              {word.definition}
            </Typography>
          )}
        </Box>
      )}

      <Stack direction={'row'} spacing={1} sx={{ pl: 5.5 }}>
        <StatusCell word={word} onSetStatus={onSetStatus} />
      </Stack>
    </Box>
  );
};

const WorkCards: React.FC<WorkCardsProps> = ({
  view,
  tableSettings,
  onSetStatus,
  onDeleteOne,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

  if (view.filtered.length === 0) {
    return <EmptyState />;
  }

  const cols = isPhone ? 1 : isTablet ? 2 : view.settings.cardsPerRow;
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.75,
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
      }}
    >
      {view.grouped.map((g) => (
        <Fragment key={g.key}>
          {g.label !== null && (
            <GroupHeader group={g} view={view} span />
          )}
          {g.words.map((w) => (
            <WordCard
              key={w.id}
              word={w}
              view={view}
              tableSettings={tableSettings}
              onSetStatus={onSetStatus}
              onDeleteOne={onDeleteOne}
            />
          ))}
        </Fragment>
      ))}
    </Box>
  );
};

export default WorkCards;
